import { useTranslation } from '@mc/i18n'
import { Container } from '../Container'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '../i18n'
import type { CurriculumSectionData } from '../types/sections'
import { CurriculumCard } from './CurriculumCard'
import { CURRICULUM_SUBJECT_LINKS } from './constants/curriculumSubjects'
import type { CurriculumSubjectItem } from './types'

interface CurriculumCopy {
  id: string
  name: string
  description: string
}

interface CurriculumSectionProps {
  data: CurriculumSectionData
  /**
   * Set false to render the subject cards as static content — no CTA, no
   * tap-through row. Used by the Status Portal, where the audience is already
   * enrolled and the subject landing pages are not a useful destination.
   */
  withSubjectLinks?: boolean
}

export const CurriculumSection = ({
  data,
  withSubjectLinks = true,
}: CurriculumSectionProps) => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)

  const rawCopy = t(data.itemsKey, { returnObjects: true })
  const copy = Array.isArray(rawCopy) ? (rawCopy as CurriculumCopy[]) : []
  const subjects: CurriculumSubjectItem[] = copy.flatMap((item) => {
    const links = CURRICULUM_SUBJECT_LINKS[item.id]
    return links ? [{ ...item, ...links }] : []
  })

  if (subjects.length === 0) return null

  const headline = t('curriculum.headline')
  const ctaLabel = t('curriculum.ctaLabel')

  return (
    <section
      id='curriculum'
      data-section='curriculum'
      className='w-full py-6 text-edu-fg-primary md:py-12'
    >
      <Container>
        <div className='flex flex-col gap-6 md:gap-4'>
          <header className='flex flex-col gap-3'>
            <div className='border-t border-edu-stroke-default' />
            <div className='flex items-center justify-between md:min-h-12'>
              <h2 className='m-0 text-xl leading-tight font-semibold capitalize md:text-title-3 md:leading-title-3'>
                {headline}
              </h2>
            </div>
          </header>

          <ul className='m-0 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-2 md:gap-4 lg:grid-cols-3 xl:grid-cols-5'>
            {subjects.map((subject) => (
              <CurriculumCard
                key={subject.id}
                subject={subject}
                ctaLabel={ctaLabel}
                withSubjectLinks={withSubjectLinks}
              />
            ))}
          </ul>

          <p className='m-0 text-mono-2 text-edu-fg-semi-dimmed'>
            {t('curriculum.disclaimer')}
          </p>
        </div>
      </Container>
    </section>
  )
}
